/*
 * Copyright (c) 2025 CalAmp Corp. All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import {makeStyles} from '@material-ui/core/styles';
import {ColDef} from 'ag-grid-community';
import {translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {resolveDefinitions} from '@ion/dls/src/grid/ColumnResolver';
import IonGrid, {IonGridAPI} from '@ion/dls/src/grid/IonGrid';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {DLSTheme} from '@ion/dls/src/types/types';
import {getTagValue, mapEventCodeToStatus} from './helpers';
import {SentinelTabType, SentinelTags} from './SentinelAssociationTab';
import SentinelEventsStatusCell from './SentinelEventsStatusCell';
import SentinelRowEditor from './SentinelRowEditor';
import {loadSentinelEvents} from './state';

interface SentinelGridProps {
  tabName?: string;
  deviceType?: any;
  activeTab?: SentinelTabType;
  currentIdentifier?: string | number;
  isRefreshed?: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  sentinelGrid : {
    '& .ag-root-wrapper' : {
      [theme.breakpoints.down('sm')] : {
        fontSize : '12px'
      }
    },
    '& .ag-header-cell-label' : {
      [theme.breakpoints.down('sm')] : {
        justifyContent : 'center'
      }
    },
    '& .ag-cell' : {
      [theme.breakpoints.down('sm')] : {
        padding    : '4px 8px',
        lineHeight : '1.2'
      }
    },
    '& .ag-column-hide-on-mobile' : {
      [theme.breakpoints.down('sm')] : {
        display : 'none'
      }
    },
    '& .ag-center-cols-viewport' : {
      overflowX                      : 'hidden !important',
      [theme.breakpoints.down('sm')] : {
        overflowX : 'scroll !important'
      }
    },
    '& .ag-popup' : {
      height                         : 'auto',
      [theme.breakpoints.down('sm')] : {
        maxHeight : '70vh'
      }
    },
    '&&' : {
      height                                 : '100%',
      '& .ag-body-viewport.ag-layout-normal' : {
        overflowY : 'auto'
      },
      '& .ag-overlay-no-rows-center' : {
        marginTop : 0
      }
    }
  }
}));

/**
 * A paginated grid component for displaying Sentinel device event history.
 *
 * - Fetches and displays paginated Sentinel event data for the selected device association.
 * - Filters events by device type using client-side logic.
 * - Dynamically adapts to grid page size and pagination events.
 * - Custom column rendering for event status, messages, and event time.
 * - Responsive column definitions for desktop and mobile layouts.
 * - Shows "No Rows" overlay when there are no events to display.
 *
 * ```
 * 
 * <SentinelGrid
 *   tabName="current"
 *   activeTab={SentinelTabType.CURRENT}
 *   deviceType="primary"
 *   currentIdentifier="6003001123"
 * />
 * 
 * ```
 *
 */
const SentinelGrid = ({
  tabName,
  activeTab,
  deviceType,
  currentIdentifier,
  isRefreshed = false
}: SentinelGridProps) => {
  const dispatch                                    = useDispatch();
  const classes                                     = useStyles();
  const ionGridRef                                  = useRef<IonGridAPI>();
  const [gridApi, setGridApi]                       = useState(null);
  const [hasItems, setHasItems]                     = useState(false);
  const [filteredTotalCount, setFilteredTotalCount] = useState(0);

  /**
   * Takes a list of Sentinel and constructs a list that can be easily
   * consumed by the IonGrid API.
   */
  const constructSentinelGridRows = useCallback((events: GenericObject[]): GridResult[] => {
    const gridItems = events?.map(event => {
      const dynamicPayload = event?.appMessage?.dynamicPayload;
      const messagesUI     = getTagValue(dynamicPayload, SentinelTags.EVENT_CODE);

      return {
        ...event,
        messagesUI,
        eventTimeUI : formatDateTime(event?.appMessage?.eventTime)
      };
    }) as GridResult[];

    setHasItems(!!gridItems.length);
    return gridItems;
  }, []);

  // Provides reference to the grid's api.
  const onGridReady = useCallback(function(api) {
    setGridApi(api);
  }, []);

  const attributeMappings = {
    eventTimeUI : 'eventTime',
    messagesUI  : 'eventCode',
    statusUI    : 'status'
  };

  const getSentinelEvents = function({
    page,
    pageSize,
    callback
  }) {
    if (currentIdentifier) {
      dispatch(loadSentinelEvents({
        page,
        tabName,
        callback,
        pageSize,
        deviceType,
        currentIdentifier,
        setFilteredTotalCount,
        sentinelDeviceType : SentinelTags.DEVICE_TYPE
      }) as any);
    } else {
      callback({
        items      : [],
        totalCount : 0
      });
    }
  };

  useEffect(() => {
    gridApi?.hideOverlay();
    if (gridApi && !hasItems) {
      gridApi?.showNoRowsOverlay();
    }
  }, [hasItems, gridApi]);

  const getGridHeight = useCallback(function() {
    return classes.sentinelGrid;
  }, []);

  return (
    <IonGrid omitHeader
      pageOffset
      paginationEnabled
      usePreferedPageSize
      showCustomPaginationBar
      key={`${activeTab}_${currentIdentifier}`}
      ref={ionGridRef}
      refreshRequired={isRefreshed}
      pageSizes={PAGE_SIZES}
      loadItems={getSentinelEvents}
      augmentItems={constructSentinelGridRows}
      attributeMappings={attributeMappings}
      columnDefinitions={getColumnDefinitions()}
      checkboxesEnabled={false}
      showHeaderCheckbox={false}
      viewSubGroups={false}
      onGridReady={onGridReady}
      getGridHeight={getGridHeight}
      totalRows={filteredTotalCount}
      columnComponents={{
        rowMenu : item => (
          <SentinelRowEditor data={item?.data} />
        ),
        statusRenderer : (data) => {
          const status = mapEventCodeToStatus(data?.data?.messagesUI);
          return <SentinelEventsStatusCell status={status} />;
        }
      }} />
  );
};

const defaultColumn = (headerName: string, field: string): ColDef => ({
  headerName,
  field,
  sortable   : false,
  filter     : false,
  resizable  : true,
  unSortIcon : false,
  hide       : false
});

const getStatus = (): ColDef => ({
  ...defaultColumn(translate('admin-devices:view.sentinel.grid.status'), 'statusUI'),
  cellRenderer : 'statusRenderer'
});

const getEventMobile = (): ColDef => ({
  ...defaultColumn(translate('admin-devices:view.sentinel.grid.eventTime'), 'eventTimeUI'),
  cellClass : 'ag-column-compact',
  width     : 120
});

const getStatusMobile = (): ColDef => ({
  ...defaultColumn(translate('admin-devices:view.sentinel.grid.status'), 'statusUI'),
  cellRenderer : 'statusRenderer',
  cellClass    : 'ag-column-compact',
  width        : 20
});

const getMessagesMobile = (): ColDef => ({
  ...defaultColumn(translate('admin-devices:view.sentinel.grid.messages'), 'messagesUI'),
  cellClass : 'ag-column-compact',
  width     : 150
});

/**
 * Returns column definitions for desktop sentinel grid
 */
const getColumnDefinitionsDesktop = (): ColDef[] => {
  return [
    defaultColumn(translate('admin-devices:view.sentinel.grid.eventTime'), 'eventTimeUI'),
    getStatus(),
    defaultColumn(translate('admin-devices:view.sentinel.grid.messages'), 'messagesUI')
  ];
};

/**
 * Returns column definitions for mobile alert history grid
 */
const getColumnDefinitionsMobile = (): ColDef[] => {
  return [
    getEventMobile(),
    getStatusMobile(),
    getMessagesMobile()
  ];
};

const getColumnDefinitions = () => ({
  desktop : resolveDefinitions(getColumnDefinitionsDesktop()),
  mobile  : resolveDefinitions(getColumnDefinitionsMobile())
});

export default React.memo(SentinelGrid);
